'use client'

// Error components must be Client Components
import { useEffect } from 'react'
import { Button } from '@mantine/core'

type AppErrorBoundaryProps = {
  error: Error & { digest?: string }
  reset: () => void
  trackError?: (error: Error | unknown, context?: { metaData?: { error: Error } }) => void
}

// `redirect()` and `notFound()` from `next/navigation` signal control flow by throwing
// sentinel errors. They are not application errors and must not be reported to telemetry.
function isNextInternalError(error: Error & { digest?: string }): boolean {
  if (error.message === 'NEXT_REDIRECT' || error.message === 'NEXT_NOT_FOUND') return true
  if (error.digest?.startsWith('NEXT_REDIRECT')) return true
  if (error.digest === 'NEXT_NOT_FOUND') return true
  return false
}

export default function AppErrorBoundary({ error, reset, trackError }: AppErrorBoundaryProps) {
  useEffect(() => {
    if (isNextInternalError(error)) return
    // Log the error to an error reporting service
    console.error(error)
    if (trackError) {
      trackError(error)
      trackError('Error caught in boundary', { metaData: { error } })
    }
  }, [error, trackError])

  const isForbiddenError =
    // @ts-expect-error - graphQLError
    error.graphQLErrors?.[0]?.extensions?.code === 'FORBIDDEN' ||
    ['access denied', 'forbidden'].some((str) => error?.message?.toLowerCase().includes(str))

  return (
    <div style={{ padding: '1em' }}>
      {isForbiddenError ? (
        <>
          <h2>Access Denied!</h2>
          <p>You may have limited access to this page. Please try again or contact support.</p>
          <Button
            onClick={
              // Reloads the current page
              () => window.location.reload()
            }
          >
            Try again
          </Button>
        </>
      ) : (
        <>
          <h2>Something went wrong!</h2>
          <p>There was an error loading the page. Please try again or contact support.</p>
          <Button
            onClick={
              // Attempt to recover by trying to re-render the segment
              () => reset()
            }
          >
            Try again
          </Button>
        </>
      )}
    </div>
  )
}
